import React, { useEffect, useState, useCallback } from "react";
import { api, formatCurrency, INVESTMENT_TYPES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { ArrowUpRight, ArrowDownRight, RefreshCw, TrendingUp, CreditCard, Handshake, Wallet, FileDown, FileText } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import { MonthlyExpensesCard, RecurringCard, InsightCard } from "@/components/DashboardAnalytics";
import { MortgageCard, RecurringAlertBanner } from "@/components/PanelWidgets";
import { BudgetsCard } from "@/pages/Budgets";
import { GoalsCard } from "@/pages/Goals";
import MonthlyRecapCard from "@/components/MonthlyRecapCard";
import MonthlyRecapPreviewDialog from "@/components/MonthlyRecapPreviewDialog";
import RebalanceDriftWidget from "@/components/RebalanceDriftWidget";
import RealEstateWidget from "@/components/RealEstateWidget";
import MobileHome from "@/components/MobileHome";
import HighValueReviewDialog from "@/components/HighValueReviewDialog";
import { checkAlerts } from "@/lib/push";
import { useTranslation } from "react-i18next";

const CHART_COLORS = ["#0F291E", "#1C7C54", "#4B9471", "#8EBFA6", "#D1E2DA", "#B8CFC4"];

// Hoisted stable style objects (avoid new refs on every render)
const AXIS_TICK = { fontSize: 12, fill: "#5F7368" };
const TOOLTIP_CONTENT_STYLE = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12, boxShadow: "0 8px 32px rgba(15,41,30,0.08)" };
const TOOLTIP_CONTENT_STYLE_BAR = { background: "#fff", border: "1px solid #EBEBEA", borderRadius: 12 };
const BAR_CURSOR = { fill: "#EBEBEA", opacity: 0.4 };
const BAR_RADIUS = [8, 8, 0, 0];

const StatCard = ({ label, value, sublabel, icon: Icon, tone, testid }) => (
  <div className="card-surface p-6 flex flex-col gap-2" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#8A9992]">
      <Icon className="w-4 h-4" />
      <span className="text-xs uppercase tracking-[0.08em] font-bold">{label}</span>
    </div>
    <div className={`font-display text-3xl font-semibold tabular-nums ${tone || "text-[#0A110E]"}`}>
      {value}
    </div>
    {sublabel && <div className="text-sm text-[#5F7368] tabular-nums">{sublabel}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hvCount, setHvCount] = useState(0);
  const [hvOpen, setHvOpen] = useState(false);
  const [recapPreviewOpen, setRecapPreviewOpen] = useState(false);
  const currency = user?.base_currency || "USD";

  const load = useCallback(async () => {
    try {
      const res = await api.get("/summary");
      setSummary(res.data);
    } catch (e) {
      logger.error("summary failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll for high-value unlinked transactions once per session.
  useEffect(() => {
    let cancelled = false;
    api.get("/transactions/high-value-unlinked", { params: { threshold: 2000 } })
      .then((r) => { if (!cancelled) setHvCount(r.data?.count || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // One-time per session: trigger daily price refresh if stale (>24h)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/investments/auto-refresh-if-stale");
        if (cancelled) return;
        if (r.data?.was_refreshed) {
          const parts = [];
          if (r.data.crypto_updated) parts.push(t("dashboard.cryptoUpdated", { count: r.data.crypto_updated }));
          if (r.data.stock_updated) parts.push(t("dashboard.stocksUpdated", { count: r.data.stock_updated }));
          if (r.data.isin_updated) parts.push(t("dashboard.isinUpdated", { count: r.data.isin_updated }));
          toast.success(
            parts.length ? t("dashboard.pricesUpdated", { parts: parts.join(" · ") }) : t("dashboard.pricesChecked")
          );
          load();
        }
      } catch (e) {
        logger.warn("auto refresh", e);
      }
      // Fire pending push alerts (idempotent server-side)
      if (!cancelled) checkAlerts();
    })();
    return () => { cancelled = true; };
  }, [load, t]);

  const refreshCrypto = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/investments/refresh-prices");
      toast.success(t("dashboard.cryptoRefreshed", { count: res.data.updated }));
      await load();
    } catch (e) {
      toast.error(t("dashboard.cryptoRefreshFailed"));
    } finally {
      setRefreshing(false);
    }
  };

  const exportPdf = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    try {
      const res = await api.get(`/reports/monthly.pdf?year=${year}&month=${month}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `patrimonio-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t("dashboard.reportDownloaded"));
    } catch (e) {
      logger.error("export pdf", e);
      toast.error(t("dashboard.reportFailed"));
    }
  };

  const exportCsv = async () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    try {
      const res = await api.get(`/reports/monthly.csv?year=${year}&month=${month}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `movimientos-${year}-${String(month).padStart(2, "0")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(t("dashboard.csvDownloaded"));
    } catch (e) {
      logger.error("export csv", e);
      toast.error(t("dashboard.csvFailed"));
    }
  };

  if (loading || !summary) {
    return <div className="text-[#5F7368]">{t("dashboard.loadingPanel")}</div>;
  }

  const allocationData = Object.entries(summary.allocation_by_type || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: k === "accounts" ? t("dashboard.accountsSub") : (INVESTMENT_TYPES.find((tt) => tt.value === k)?.label || k),
      value: Number(v),
    }));

  const balanceData = [
    { name: t("dashboard.accountsChart"), value: summary.total_accounts || 0, fill: "#4B9471" },
    { name: t("dashboard.investmentsChart"), value: summary.total_investments, fill: "#1C7C54" },
    { name: t("dashboard.loansReceivable"), value: summary.total_loans, fill: "#8EBFA6" },
    { name: t("dashboard.debtsChart"), value: summary.total_debts, fill: "#D95D39" },
  ];

  const gainPositive = summary.gain_loss >= 0;

  return (
    <div className="flex flex-col gap-12">
      {/* Mobile-only compact home cards */}
      <MobileHome />

      {/* Header */}
      <div id="full" className="flex flex-col md:flex-row md:items-end justify-between gap-6 fade-up scroll-mt-24">
        <div>
          <p className="uppercase text-xs tracking-[0.2em] text-[#8A9992] mb-2">{t("dashboard.kicker")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A110E]">
            {t("dashboard.hello", { name: user?.name?.split(" ")[0] || t("dashboard.helloFallback") })}
          </h1>
          <p className="text-[#5F7368] mt-2">{t("dashboard.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setRecapPreviewOpen(true)}
            data-testid="preview-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors"
          >
            <FileText className="w-4 h-4" />
            {t("recap.preview", { defaultValue: "Preview PDF" })}
          </button>
          <button
            onClick={exportPdf}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            {t("dashboard.exportPdf")}
          </button>
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors"
          >
            <FileText className="w-4 h-4" />
            {t("dashboard.csv")}
          </button>
          <button
            onClick={refreshCrypto}
            data-testid="refresh-prices-btn"
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {t("dashboard.refreshCrypto")}
          </button>
        </div>
      </div>

      {/* High-value unlinked transactions banner */}
      {hvCount > 0 && (
        <button
          type="button"
          data-testid="hv-banner"
          onClick={() => setHvOpen(true)}
          className="text-left card-surface p-4 border-[#C89A3F]/30 bg-[#C89A3F]/5 hover:bg-[#C89A3F]/10 transition-colors flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-[#C89A3F]/15 flex items-center justify-center shrink-0">
            <span className="text-lg">🏠</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#0A110E]">
              {t("dashboard.hvBannerTitle", { count: hvCount })}
            </div>
            <div className="text-xs text-[#5F7368]">
              {t("dashboard.hvBannerDesc")}
            </div>
          </div>
          <div className="text-xs font-semibold text-[#8B6B1F]">{t("dashboard.hvBannerReview")}</div>
        </button>
      )}

      {/* Hero net worth */}
      <div className="card-surface p-8 md:p-12 fade-up relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1657023647725-7a3616ea54af?w=1400&q=80')", backgroundSize: "cover" }} />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992] mb-3">{t("dashboard.netWorth")}</p>
          <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-8">
            <div
              data-testid="net-worth-metric"
              className={`font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tabular-nums tracking-tighter ${
                summary.net_worth >= 0 ? "text-[#0A110E]" : "text-[#D95D39]"
              }`}
            >
              {formatCurrency(summary.net_worth, currency)}
            </div>
            <div className={`flex items-center gap-1 text-sm font-medium ${gainPositive ? "text-[#1C7C54]" : "text-[#D95D39]"}`}>
              {gainPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span className="tabular-nums">
                {formatCurrency(Math.abs(summary.gain_loss), currency)} ({summary.gain_loss_pct?.toFixed(2)}%) {t("dashboard.returnLabel")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 fade-up">
        <StatCard
          label={t("dashboard.accounts")}
          value={formatCurrency(summary.total_accounts || 0, currency)}
          sublabel={t("dashboard.accountsCount", { count: summary.counts.accounts || 0 })}
          icon={Wallet}
          tone="text-[#0A110E]"
          testid="stat-accounts"
        />
        <StatCard
          label={t("dashboard.investments")}
          value={formatCurrency(summary.total_investments, currency)}
          sublabel={t("dashboard.assetsCount", { count: summary.counts.investments })}
          icon={TrendingUp}
          tone="text-[#1C7C54]"
          testid="stat-investments"
        />
        <StatCard
          label={t("dashboard.debts")}
          value={formatCurrency(summary.total_debts, currency)}
          sublabel={t("dashboard.pendingCount", { count: summary.counts.debts })}
          icon={CreditCard}
          tone="text-[#D95D39]"
          testid="stat-debts"
        />
        <StatCard
          label={t("dashboard.loans")}
          value={formatCurrency(summary.total_loans, currency)}
          sublabel={t("dashboard.activeCount", { count: summary.counts.loans })}
          icon={Handshake}
          testid="stat-loans"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 fade-up">
        <div className="card-surface p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("dashboard.distribution")}</p>
              <h3 className="font-display text-lg font-semibold text-[#0A110E]">{t("dashboard.allocationByType")}</h3>
            </div>
            <Wallet className="w-4 h-4 text-[#8A9992]" />
          </div>
          <div className="h-64 w-full min-w-0" data-testid="allocation-pie">
            {allocationData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#8A9992]">
                {t("dashboard.noAssets")}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocationData}
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={2}
                    dataKey="value"
                    animationDuration={800}
                  >
                    {allocationData.map((d, i) => (
                      <Cell key={d.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => formatCurrency(v, currency)}
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          {allocationData.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {allocationData.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-xs text-[#5F7368]">
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="flex-1 truncate">{d.name}</span>
                  <span className="tabular-nums font-medium text-[#0A110E]">{formatCurrency(d.value, currency)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card-surface p-6 lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.08em] font-bold text-[#8A9992]">{t("dashboard.balance")}</p>
              <h3 className="font-display text-lg font-semibold text-[#0A110E]">{t("dashboard.assetsVsDebts")}</h3>
            </div>
          </div>
          <div className="h-64 w-full min-w-0" data-testid="balance-bar">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={balanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBEBEA" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={70}
                  tickFormatter={(v) => formatCurrency(v, currency)}
                />
                <Tooltip
                  formatter={(v) => formatCurrency(v, currency)}
                  contentStyle={TOOLTIP_CONTENT_STYLE_BAR}
                  cursor={BAR_CURSOR}
                />
                <Bar dataKey="value" radius={BAR_RADIUS} animationDuration={700} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* AI Insight + Recurring alert banner */}
      <div className="flex flex-col gap-4 fade-up">
        <RecurringAlertBanner />
        <InsightCard />
      </div>

      {/* Mortgage widget + Budgets + Goals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up">
        <MortgageCard />
        <BudgetsCard />
        <GoalsCard />
      </div>

      {/* Real estate widget */}
      <div className="fade-up">
        <RealEstateWidget />
      </div>

      {/* Rebalance drift indicator */}
      <div className="fade-up">
        <RebalanceDriftWidget />
      </div>

      {/* End-of-month recap */}
      <div className="fade-up">
        <MonthlyRecapCard />
      </div>

      {/* Monthly expenses + recurring */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 fade-up">
        <div className="lg:col-span-3">
          <MonthlyExpensesCard />
        </div>
        <div className="lg:col-span-2">
          <RecurringCard />
        </div>
      </div>

      <HighValueReviewDialog
        open={hvOpen}
        onOpenChange={setHvOpen}
        threshold={2000}
      />
      <MonthlyRecapPreviewDialog
        open={recapPreviewOpen}
        onOpenChange={setRecapPreviewOpen}
      />
    </div>
  );
}
